import { useInfiniteQuery, useQueries } from '@tanstack/react-query';
import { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import {
  IResourcePlannerItem,
  IResourcePlannerResult,
} from '@legacy/apis/types/resourcePlannerAPI';
import { IFilterStoreProps } from '@legacy/reducers/FilterReducer';
import { TCollapseExpandState } from '@legacy/screens/ResourcePlanner/helper/expandedRow';
import { RPRow } from '@legacy/screens/ResourcePlanner/types/resourcePlanner';
import { postFetch } from '@legacy/utils/fetcher';
import {
  GROUP_BY_RESOURCE_KEY,
  PARTIAL_GROUP_BY_RESOURCE_KEY,
} from '../constants';
import { getFiltersParam, getQueryParams } from '../utils';

interface IGroupByResourceBase {
  params: Record<string, string>;
  selectedFilterList: IFilterStoreProps['selectedFilterList'];
}

const getTranslatedStatus = (
  result: IResourcePlannerItem[],
  t: TFunction<'resourcePlanner', undefined>,
) =>
  result.map(({ children, id, ...child }): RPRow => {
    const parentList = child.pathToParent?.split('/')?.filter(Boolean);
    return {
      ...child,
      children: children ? getTranslatedStatus(children, t) : undefined,
      id: `${child.resourceId}_${child.workItemId}`,
      parentId: (() => {
        if (parentList?.length) {
          return `${child.resourceId}_${parentList[parentList.length - 1]}`;
        }
        return child.hierarchyType === 'workitem'
          ? `${child.resourceId}_0`
          : child.parentId;
      })(),
      translatedStatus: t(child.status),
    };
  });

// Fetch resource list

const GROUP_BY_RESOURCE_URL_PATH =
  '/api/v2/resource-planner/partial-group-by-employee-first-load';

const resourcePlannerFetch = (
  filters: string,
  params: Record<string, string>,
): Promise<IResourcePlannerResult> =>
  postFetch({
    path: `${GROUP_BY_RESOURCE_URL_PATH}${getQueryParams(params)}`.concat(
      filters,
    ),
    key: GROUP_BY_RESOURCE_KEY,
  });

interface IUseGetGroupByResource extends IGroupByResourceBase {
  take: number;
}

export const useGetGroupByResource = ({
  params,
  selectedFilterList,
  take,
}: IUseGetGroupByResource) => {
  const { t } = useTranslation('resourcePlanner');
  const filtersParam = getFiltersParam(selectedFilterList);

  return useInfiniteQuery<IResourcePlannerResult>({
    initialPageParam: 0,
    getNextPageParam: (_lastGroup, groups) => groups.length,
    queryFn: async ({ pageParam }) => {
      const reqParams: Record<string, string> = {
        ...params,
        skip: String(typeof pageParam === 'number' ? pageParam * take : 0),
        take: String(take),
      };
      const response = await resourcePlannerFetch(filtersParam, reqParams);
      if (response?.model?.properties?.children) {
        response.model.properties.children = getTranslatedStatus(
          response.model.properties.children,
          t,
        );
      }
      return response;
    },
    queryKey: [GROUP_BY_RESOURCE_KEY, params, selectedFilterList],
    refetchOnWindowFocus: false,
  });
};

// Fetch resource children

const PARTIAL_GROUP_BY_RESOURCE_URL_PATH =
  '/api/v2/resource-planner/partial-group-by-employee';

const partialResourcePlannerFetch = (
  filters: string,
  params: Record<string, string>,
  expandId: string,
  expandType: IResourcePlannerItem['type'],
  resourceIdRoot?: string,
): Promise<IResourcePlannerResult> =>
  postFetch({
    path: `${PARTIAL_GROUP_BY_RESOURCE_URL_PATH}${getQueryParams(params)}`.concat(
      filters,
    ),
    key: PARTIAL_GROUP_BY_RESOURCE_KEY,
    body: JSON.stringify({ expandId, expandType, expandedIds: resourceIdRoot }),
  });

interface IUseGetPartialGroupByResource extends IGroupByResourceBase {
  expandedRows: TCollapseExpandState[];
}

export const useGetPartialGroupByResource = ({
  expandedRows,
  params,
  selectedFilterList,
}: IUseGetPartialGroupByResource) => {
  const { t } = useTranslation('resourcePlanner');
  const filtersParam = getFiltersParam(selectedFilterList);

  return useQueries({
    queries: expandedRows.map(
      ({ originalId: rowId, resourceIdRoot, tableRowId, type: rowType }) => ({
        keepPreviousData: true,
        queryFn: async () => {
          const response = await partialResourcePlannerFetch(
            filtersParam,
            params,
            rowId,
            rowType,
            resourceIdRoot,
          );
          if (response?.model?.properties?.children) {
            response.model.properties.children = getTranslatedStatus(
              response.model.properties.children,
              t,
            );
          }
          return response;
        },
        queryKey: [
          PARTIAL_GROUP_BY_RESOURCE_KEY,
          tableRowId,
          params,
          selectedFilterList,
        ],
      }),
    ),
  });
};
